import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  LogOut, Calendar, Clock, MapPin, Mail, Phone, Settings,
  CheckCircle, XCircle, AlertCircle, MessageSquare, Plus, Save,
  User, Edit, Eye, TrendingUp, Users, IndianRupee, Trash2, Package,
  CreditCard, Building
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AgencyDashboard = () => {
  const { user, logout, isAuthenticated, updateRole } = useAuth();
  const navigate = useNavigate();
  const [agency, setAgency] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [chats, setChats] = useState([]);
  const [specializations, setSpecializations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateAgency, setShowCreateAgency] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [showManagePlans, setShowManagePlans] = useState(false);
  const [showBankDetails, setShowBankDetails] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // Agency form state
  const [agencyForm, setAgencyForm] = useState({
    name: '',
    city: '',
    address: '',
    phone: '',
    description: '',
    specializations: [],
    years_experience: 0,
    base_price: 5000,
    plans: []
  });

  // Separate state for plans editing (to avoid state conflicts)
  const [editingPlans, setEditingPlans] = useState([]);

  // Bank details form
  const [bankForm, setBankForm] = useState({
    bank_account_name: '',
    bank_account_number: '',
    bank_ifsc: '',
    bank_name: '',
    upi_id: ''
  });

  // Plan form state
  const [planForm, setPlanForm] = useState({
    name: '',
    description: '',
    price: '',
    duration: 'Per Case',
    features: ''
  });

  const cities = ['Mumbai', 'Delhi', 'Bangalore', 'Chennai', 'Kolkata', 'Hyderabad', 'Pune', 'Ahmedabad', 'Jaipur', 'Lucknow'];

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    const fetchData = async () => {
      try {
        const specsRes = await axios.get(`${API}/specializations`);
        setSpecializations(specsRes.data.specializations);

        // Check if agency exists
        try {
          const agencyRes = await axios.get(`${API}/agencies/my/profile`, { withCredentials: true });
          setAgency(agencyRes.data);
          setAgencyForm({
            name: agencyRes.data.name || '',
            city: agencyRes.data.city || '',
            address: agencyRes.data.address || '',
            phone: agencyRes.data.phone || '',
            description: agencyRes.data.description || '',
            specializations: agencyRes.data.specializations || [],
            years_experience: agencyRes.data.years_experience || 0,
            base_price: agencyRes.data.base_price || 5000,
            plans: agencyRes.data.plans || []
          });
          
          // Set bank details form
          setBankForm({
            bank_account_name: agencyRes.data.bank_account_name || '',
            bank_account_number: agencyRes.data.bank_account_number || '',
            bank_ifsc: agencyRes.data.bank_ifsc || '',
            bank_name: agencyRes.data.bank_name || '',
            upi_id: agencyRes.data.upi_id || ''
          });
          
          // Fetch bookings, inquiries, and chats
          const [bookingsRes, inquiriesRes, chatsRes] = await Promise.all([
            axios.get(`${API}/bookings/agency`, { withCredentials: true }),
            axios.get(`${API}/inquiries/agency`, { withCredentials: true }),
            axios.get(`${API}/chats`, { withCredentials: true })
          ]);
          
          setBookings(bookingsRes.data);
          setInquiries(inquiriesRes.data);
          setChats(chatsRes.data);
        } catch (error) {
          if (error.response?.status === 404) {
            setShowCreateAgency(true);
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        if (error.response?.status === 401) {
          navigate('/login');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isAuthenticated, navigate]);

  const handleCreateAgency = async () => {
    if (!agencyForm.name || !agencyForm.city) {
      toast.error('Please fill required fields');
      return;
    }

    try {
      const response = await axios.post(`${API}/agencies`, agencyForm, { withCredentials: true });
      setAgency(response.data);
      setShowCreateAgency(false);
      toast.success('Agency created successfully!');
      
      // Update role to agency
      await updateRole('agency');
    } catch (error) {
      console.error('Error creating agency:', error);
      toast.error(error.response?.data?.detail || 'Failed to create agency');
    }
  };

  const handleUpdateAgency = async () => {
    if (!agencyForm.name || !agencyForm.city) {
      toast.error('Please fill required fields');
      return;
    }

    try {
      // Use editingPlans if in Manage Plans dialog
      const plansToSave = showManagePlans ? editingPlans : agencyForm.plans;
      const dataToSend = { ...agencyForm, plans: plansToSave };
      
      console.log('Saving agency. Plans count:', plansToSave.length);
      
      const response = await axios.put(`${API}/agencies/${agency.agency_id}`, dataToSend, { withCredentials: true });
      console.log('Save successful. Plans in response:', response.data.plans?.length);
      
      setAgency(response.data);
      setAgencyForm({
        name: response.data.name || '',
        city: response.data.city || '',
        address: response.data.address || '',
        phone: response.data.phone || '',
        description: response.data.description || '',
        specializations: response.data.specializations || [],
        years_experience: response.data.years_experience || 0,
        base_price: response.data.base_price || 5000,
        plans: response.data.plans || []
      });
      setEditingPlans(response.data.plans || []);
      setShowEditProfile(false);
      setShowManagePlans(false);
      toast.success('Profile updated successfully!');
    } catch (error) {
      console.error('Error updating agency:', error);
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    }
  };

  const handleAddPlan = () => {
    if (!planForm.name || !planForm.price) {
      toast.error('Please fill plan name and price');
      return;
    }

    const newPlan = {
      plan_id: `plan_${Date.now()}`,
      name: planForm.name,
      description: planForm.description,
      price: parseFloat(planForm.price),
      duration: planForm.duration,
      features: planForm.features.split('\n').filter(f => f.trim())
    };

    console.log('Adding plan:', newPlan.name, '₹' + newPlan.price);
    setEditingPlans(prev => [...prev, newPlan]);
    setPlanForm({ name: '', description: '', price: '', duration: 'Per Case', features: '' });
    toast.success('Plan added! Click "Save All Plans" to save.');
  };

  const handleRemovePlan = (planId) => {
    setEditingPlans(prev => prev.filter(p => p.plan_id !== planId));
    toast.success('Plan removed! Click "Save All Plans" to save.');
  };

  const openManagePlans = async () => {
    try {
      const agencyRes = await axios.get(`${API}/agencies/my/profile`, { withCredentials: true });
      console.log('Loaded plans:', agencyRes.data.plans?.length);
      setAgency(agencyRes.data);
      setAgencyForm({
        name: agencyRes.data.name || '',
        city: agencyRes.data.city || '',
        address: agencyRes.data.address || '',
        phone: agencyRes.data.phone || '',
        description: agencyRes.data.description || '',
        specializations: agencyRes.data.specializations || [],
        years_experience: agencyRes.data.years_experience || 0,
        base_price: agencyRes.data.base_price || 5000,
        plans: agencyRes.data.plans || []
      });
      setEditingPlans(agencyRes.data.plans || []);
      setShowManagePlans(true);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to load data. Please try again.');
    }
  };

  const openEditProfile = async () => {
    try {
      const agencyRes = await axios.get(`${API}/agencies/my/profile`, { withCredentials: true });
      setAgency(agencyRes.data);
      setAgencyForm({
        name: agencyRes.data.name || '',
        city: agencyRes.data.city || '',
        address: agencyRes.data.address || '',
        phone: agencyRes.data.phone || '',
        description: agencyRes.data.description || '',
        specializations: agencyRes.data.specializations || [],
        years_experience: agencyRes.data.years_experience || 0,
        base_price: agencyRes.data.base_price || 5000,
        plans: agencyRes.data.plans || []
      });
      setEditingPlans(agencyRes.data.plans || []);
      setShowEditProfile(true);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to load data. Please try again.');
    }
  };

  const handleUpdateBankDetails = async () => {
    // Validate at least one field is filled
    if (!bankForm.bank_account_name && !bankForm.bank_account_number && !bankForm.upi_id) {
      toast.error('Please fill at least account holder name and account number, or UPI ID');
      return;
    }
    
    // If bank account is provided, require all bank fields
    if (bankForm.bank_account_number && (!bankForm.bank_account_name || !bankForm.bank_ifsc)) {
      toast.error('Please fill account holder name, account number, and IFSC code');
      return;
    }
    
    try {
      const response = await axios.put(`${API}/agencies/bank-details`, bankForm, { withCredentials: true });
      console.log('Bank details update response:', response.data);
      toast.success('Bank details updated successfully!');
      setShowBankDetails(false);
      // Refresh agency data to show updated bank details
      const agencyRes = await axios.get(`${API}/agencies/my/profile`, { withCredentials: true });
      setAgency(agencyRes.data);
      setBankForm({
        bank_account_name: agencyRes.data.bank_account_name || '',
        bank_account_number: agencyRes.data.bank_account_number || '',
        bank_ifsc: agencyRes.data.bank_ifsc || '',
        bank_name: agencyRes.data.bank_name || '',
        upi_id: agencyRes.data.upi_id || ''
      });
    } catch (error) {
      console.error('Error updating bank details:', error);
      console.error('Error response:', error.response?.data);
      const errorMessage = error.response?.data?.detail || 'Failed to update bank details. Please try again.';
      toast.error(errorMessage);
    }
  };

  const handleUpdateBookingStatus = async (bookingId, newStatus) => {
    try {
      await axios.put(`${API}/bookings/${bookingId}/status`, { status: newStatus }, { withCredentials: true });
      setBookings(bookings.map(b => 
        b.booking_id === bookingId ? { ...b, status: newStatus } : b
      ));
      toast.success('Booking status updated');
    } catch (error) {
      console.error('Error updating booking:', error);
      toast.error('Failed to update status');
    }
  };

  const handleUpdateInquiryStatus = async (inquiryId, newStatus) => {
    try {
      await axios.put(`${API}/inquiries/${inquiryId}/status`, { status: newStatus }, { withCredentials: true });
      setInquiries(inquiries.map(i => 
        i.inquiry_id === inquiryId ? { ...i, status: newStatus } : i
      ));
      toast.success('Inquiry status updated');
    } catch (error) {
      console.error('Error updating inquiry:', error);
      toast.error('Failed to update status');
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(price);
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-50 text-amber-700 border-amber-200',
      confirmed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      completed: 'bg-slate-100 text-slate-700 border-slate-200',
      cancelled: 'bg-rose-50 text-rose-700 border-rose-200',
      new: 'bg-blue-50 text-blue-700 border-blue-200',
      responded: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      closed: 'bg-slate-100 text-slate-700 border-slate-200'
    };
    return styles[status] || styles.pending;
  };

  // Calculate statistics
  const totalRevenue = bookings.filter(b => b.payment_status === 'paid').reduce((sum, b) => sum + (b.amount || 0), 0);
  const pendingBookings = bookings.filter(b => b.status === 'pending').length;
  const confirmedBookings = bookings.filter(b => b.status === 'confirmed').length;
  const completedBookings = bookings.filter(b => b.status === 'completed').length;
  const newInquiries = inquiries.filter(i => i.status === 'new').length;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="agency-dashboard">
      {/* Navigation */}
      <nav className="sticky top-0 bg-white border-b border-slate-200 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="font-display text-xl font-bold text-slate-900 tracking-tight">
              My Detective
            </Link>
            
            <div className="flex items-center gap-4">
              <Link to="/" className="text-sm text-slate-600 hover:text-slate-900">
                Home
              </Link>
              <Link to="/chats" className="text-sm text-slate-600 hover:text-slate-900 flex items-center gap-1">
                <MessageSquare className="w-4 h-4" />
                Messages
                {chats.filter(c => c.unread_agency > 0).length > 0 && (
                  <span className="bg-emerald-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {chats.filter(c => c.unread_agency > 0).length}
                  </span>
                )}
              </Link>
              
              {/* User Profile Dropdown */}
              <div className="flex items-center gap-3 pl-4 border-l border-slate-200">
                <div className="text-right hidden sm:block">
                  <p className="text-sm font-medium text-slate-900">{user?.name}</p>
                  <p className="text-xs text-slate-500">Agency Account</p>
                </div>
                <div className="h-10 w-10 rounded-full bg-emerald-100 overflow-hidden flex items-center justify-center">
                  {user?.picture ? (
                    <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-5 h-5 text-emerald-700" />
                  )}
                </div>
                <Button 
                  variant="outline"
                  size="sm"
                  onClick={logout}
                  className="rounded-none border-slate-200 text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </Button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {agency ? (
          <>
            {/* Agency Header with Edit Button */}
            <div className="bg-white border border-slate-200 p-6 mb-8">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-emerald-100 rounded-none flex items-center justify-center">
                    <User className="w-8 h-8 text-emerald-700" />
                  </div>
                  <div>
                    <h1 className="font-display text-2xl font-bold text-slate-900 tracking-tight">
                      {agency.name}
                    </h1>
                    <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {agency.city}
                      </span>
                      {agency.phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="w-4 h-4" />
                          {agency.phone}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {agency.years_experience} years exp.
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    variant="outline"
                    onClick={() => navigate(`/agency/${agency.agency_id}`)}
                    className="rounded-none border-slate-200"
                    data-testid="view-public-profile-btn"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    View Profile
                  </Button>
                  <Button
                    variant="outline"
                    onClick={openManagePlans}
                    className="rounded-none border-slate-200"
                    data-testid="manage-plans-btn"
                  >
                    <Package className="w-4 h-4 mr-2" />
                    Plans
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setShowBankDetails(true)}
                    className="rounded-none border-slate-200"
                    data-testid="bank-details-btn"
                  >
                    <CreditCard className="w-4 h-4 mr-2" />
                    Bank Details
                  </Button>
                  <Button
                    onClick={openEditProfile}
                    className="rounded-none bg-slate-900 text-white hover:bg-slate-800"
                    data-testid="edit-profile-btn"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Edit Profile
                  </Button>
                </div>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
              <div className="bg-white border border-slate-200 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-4 h-4 text-blue-600" />
                  <p className="text-xs tracking-[0.1em] uppercase font-bold text-slate-500">Interested</p>
                </div>
                <p className="font-display text-2xl font-bold text-slate-900">{inquiries.length}</p>
                <p className="text-xs text-slate-500">{newInquiries} new</p>
              </div>
              <div className="bg-white border border-slate-200 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Calendar className="w-4 h-4 text-amber-600" />
                  <p className="text-xs tracking-[0.1em] uppercase font-bold text-slate-500">Pending</p>
                </div>
                <p className="font-display text-2xl font-bold text-amber-600">{pendingBookings}</p>
                <p className="text-xs text-slate-500">awaiting action</p>
              </div>
              <div className="bg-white border border-slate-200 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-4 h-4 text-emerald-600" />
                  <p className="text-xs tracking-[0.1em] uppercase font-bold text-slate-500">Confirmed</p>
                </div>
                <p className="font-display text-2xl font-bold text-emerald-600">{confirmedBookings}</p>
                <p className="text-xs text-slate-500">in progress</p>
              </div>
              <div className="bg-white border border-slate-200 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-4 h-4 text-slate-600" />
                  <p className="text-xs tracking-[0.1em] uppercase font-bold text-slate-500">Completed</p>
                </div>
                <p className="font-display text-2xl font-bold text-slate-900">{completedBookings}</p>
                <p className="text-xs text-slate-500">cases solved</p>
              </div>
              <div className="bg-white border border-slate-200 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <IndianRupee className="w-4 h-4 text-emerald-600" />
                  <p className="text-xs tracking-[0.1em] uppercase font-bold text-slate-500">Revenue</p>
                </div>
                <p className="font-display text-2xl font-bold text-emerald-600">{formatPrice(totalRevenue)}</p>
                <p className="text-xs text-slate-500">total earned</p>
              </div>
            </div>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="bg-white border border-slate-200">
              <TabsList className="w-full border-b border-slate-200 rounded-none bg-transparent p-0 h-auto">
                <TabsTrigger 
                  value="overview" 
                  className="rounded-none px-6 py-4 data-[state=active]:bg-slate-50 data-[state=active]:border-b-2 data-[state=active]:border-slate-900"
                  data-testid="overview-tab"
                >
                  Overview
                </TabsTrigger>
                <TabsTrigger 
                  value="bookings" 
                  className="rounded-none px-6 py-4 data-[state=active]:bg-slate-50 data-[state=active]:border-b-2 data-[state=active]:border-slate-900"
                  data-testid="bookings-tab"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Bookings ({bookings.length})
                </TabsTrigger>
                <TabsTrigger 
                  value="inquiries"
                  className="rounded-none px-6 py-4 data-[state=active]:bg-slate-50 data-[state=active]:border-b-2 data-[state=active]:border-slate-900"
                  data-testid="inquiries-tab"
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Inquiries ({inquiries.length})
                </TabsTrigger>
              </TabsList>

              {/* Overview Tab */}
              <TabsContent value="overview" className="p-6 mt-0">
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Recent Bookings */}
                  <div>
                    <h3 className="font-display text-lg font-bold text-slate-900 mb-4">Recent Bookings</h3>
                    {bookings.length === 0 ? (
                      <p className="text-slate-500 text-sm">No bookings yet</p>
                    ) : (
                      <div className="space-y-3">
                        {bookings.slice(0, 5).map((booking) => (
                          <div key={booking.booking_id} className="border border-slate-200 p-3">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium text-slate-900">{booking.user_name}</p>
                                <p className="text-xs text-slate-500">{booking.service_type}</p>
                              </div>
                              <span className={`text-xs px-2 py-0.5 border ${getStatusBadge(booking.status)}`}>
                                {booking.status}
                              </span>
                            </div>
                            <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                              <span>{booking.date}</span>
                              <span>{formatPrice(booking.amount)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Recent Inquiries */}
                  <div>
                    <h3 className="font-display text-lg font-bold text-slate-900 mb-4">Recent Inquiries</h3>
                    {inquiries.length === 0 ? (
                      <p className="text-slate-500 text-sm">No inquiries yet</p>
                    ) : (
                      <div className="space-y-3">
                        {inquiries.slice(0, 5).map((inquiry) => (
                          <div key={inquiry.inquiry_id} className="border border-slate-200 p-3">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium text-slate-900">{inquiry.name}</p>
                                <p className="text-xs text-slate-500">{inquiry.email}</p>
                              </div>
                              <span className={`text-xs px-2 py-0.5 border ${getStatusBadge(inquiry.status)}`}>
                                {inquiry.status}
                              </span>
                            </div>
                            <p className="text-sm text-slate-600 mt-2 line-clamp-2">{inquiry.message}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>

              {/* Bookings Tab */}
              <TabsContent value="bookings" className="p-0 mt-0">
                {bookings.length === 0 ? (
                  <div className="p-12 text-center">
                    <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-600">No bookings yet</p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-200">
                    {bookings.map((booking) => (
                      <div 
                        key={booking.booking_id} 
                        className="p-6"
                        data-testid={`booking-${booking.booking_id}`}
                      >
                        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                          <div className="space-y-3 flex-1">
                            {/* Client Info */}
                            <div className="bg-slate-50 border border-slate-200 p-4">
                              <p className="text-xs tracking-[0.1em] uppercase font-bold text-slate-500 mb-2">Client Details</p>
                              <div className="flex items-center gap-2 mb-2">
                                <h3 className="font-display text-lg font-bold text-slate-900">
                                  {booking.user_name}
                                </h3>
                                <span className={`text-xs px-2 py-0.5 border ${getStatusBadge(booking.status)}`}>
                                  {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                                </span>
                                {booking.payment_status === 'paid' && (
                                  <span className="text-xs px-2 py-0.5 border bg-emerald-50 text-emerald-700 border-emerald-200">
                                    <CheckCircle className="w-3 h-3 inline mr-1" />
                                    Paid
                                  </span>
                                )}
                              </div>
                              
                              <div className="grid grid-cols-2 gap-2 text-sm">
                                <div className="flex items-center gap-2 text-slate-600">
                                  <Mail className="w-4 h-4" />
                                  {booking.user_email}
                                </div>
                                {booking.contact_phone && (
                                  <div className="flex items-center gap-2 text-slate-600">
                                    <Phone className="w-4 h-4" />
                                    {booking.contact_phone}
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Booking Details */}
                            <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                              <div className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                {booking.date}
                              </div>
                              <div className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {booking.service_type}
                              </div>
                            </div>

                            <div className="bg-slate-50 p-3 border border-slate-200">
                              <p className="text-xs tracking-[0.1em] uppercase font-bold text-slate-500 mb-1">Case Description</p>
                              <p className="text-sm text-slate-700">{booking.description}</p>
                            </div>
                          </div>

                          <div className="flex flex-col items-end gap-3">
                            <p className="font-display text-xl font-bold text-slate-900">
                              {formatPrice(booking.amount)}
                            </p>
                            
                            <div className="flex items-center gap-2">
                              {booking.status === 'pending' && (
                                <>
                                  <Button
                                    size="sm"
                                    onClick={() => handleUpdateBookingStatus(booking.booking_id, 'confirmed')}
                                    className="rounded-none bg-emerald-600 text-white hover:bg-emerald-700"
                                    data-testid={`confirm-booking-${booking.booking_id}`}
                                  >
                                    <CheckCircle className="w-4 h-4 mr-1" />
                                    Confirm
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleUpdateBookingStatus(booking.booking_id, 'cancelled')}
                                    className="rounded-none border-rose-200 text-rose-700 hover:bg-rose-50"
                                    data-testid={`cancel-booking-${booking.booking_id}`}
                                  >
                                    <XCircle className="w-4 h-4 mr-1" />
                                    Decline
                                  </Button>
                                </>
                              )}
                              {booking.status === 'confirmed' && (
                                <Button
                                  size="sm"
                                  onClick={() => handleUpdateBookingStatus(booking.booking_id, 'completed')}
                                  className="rounded-none bg-slate-900 text-white hover:bg-slate-800"
                                  data-testid={`complete-booking-${booking.booking_id}`}
                                >
                                  Mark Completed
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Inquiries Tab */}
              <TabsContent value="inquiries" className="p-0 mt-0">
                {inquiries.length === 0 ? (
                  <div className="p-12 text-center">
                    <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-600">No inquiries yet</p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-200">
                    {inquiries.map((inquiry) => (
                      <div 
                        key={inquiry.inquiry_id} 
                        className="p-6"
                        data-testid={`inquiry-${inquiry.inquiry_id}`}
                      >
                        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                          <div className="space-y-3 flex-1">
                            {/* Contact Info */}
                            <div className="bg-blue-50 border border-blue-200 p-4">
                              <p className="text-xs tracking-[0.1em] uppercase font-bold text-blue-600 mb-2">Interested Client</p>
                              <div className="flex items-center gap-2 mb-2">
                                <h3 className="font-display text-lg font-bold text-slate-900">
                                  {inquiry.name}
                                </h3>
                                <span className={`text-xs px-2 py-0.5 border ${getStatusBadge(inquiry.status)}`}>
                                  {inquiry.status.charAt(0).toUpperCase() + inquiry.status.slice(1)}
                                </span>
                              </div>
                              
                              <div className="grid grid-cols-2 gap-2 text-sm">
                                <div className="flex items-center gap-2 text-slate-600">
                                  <Mail className="w-4 h-4" />
                                  {inquiry.email}
                                </div>
                                <div className="flex items-center gap-2 text-slate-600">
                                  <Phone className="w-4 h-4" />
                                  {inquiry.phone}
                                </div>
                              </div>
                            </div>

                            <div className="bg-slate-50 p-3 border border-slate-200">
                              <p className="text-xs tracking-[0.1em] uppercase font-bold text-slate-500 mb-1">Message</p>
                              <p className="text-sm text-slate-700">{inquiry.message}</p>
                            </div>
                          </div>

                          <div className="flex items-center gap-2">
                            {inquiry.status === 'new' && (
                              <Button
                                size="sm"
                                onClick={() => handleUpdateInquiryStatus(inquiry.inquiry_id, 'responded')}
                                className="rounded-none bg-emerald-600 text-white hover:bg-emerald-700"
                                data-testid={`respond-inquiry-${inquiry.inquiry_id}`}
                              >
                                Mark Responded
                              </Button>
                            )}
                            {inquiry.status !== 'closed' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleUpdateInquiryStatus(inquiry.inquiry_id, 'closed')}
                                className="rounded-none border-slate-200"
                                data-testid={`close-inquiry-${inquiry.inquiry_id}`}
                              >
                                Close
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </>
        ) : (
          <div className="text-center py-12 bg-white border border-slate-200">
            <User className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h2 className="font-display text-xl font-bold text-slate-900 mb-2">Welcome to My Detective</h2>
            <p className="text-slate-600 mb-6">Create your agency profile to start receiving bookings</p>
            <Button 
              onClick={() => setShowCreateAgency(true)}
              className="bg-slate-900 text-white hover:bg-slate-800 rounded-none"
              data-testid="create-agency-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Agency Profile
            </Button>
          </div>
        )}
      </div>

      {/* Create/Edit Agency Dialog */}
      <Dialog open={showCreateAgency || showEditProfile} onOpenChange={(open) => {
        if (!open) {
          setShowCreateAgency(false);
          setShowEditProfile(false);
        }
      }}>
        <DialogContent className="rounded-none border-slate-200 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-bold text-slate-900">
              {showEditProfile ? 'Edit Agency Profile' : 'Create Your Agency Profile'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500 mb-2 block">
                  Agency Name *
                </label>
                <Input
                  value={agencyForm.name}
                  onChange={(e) => setAgencyForm({...agencyForm, name: e.target.value})}
                  placeholder="Your Agency Name"
                  className="rounded-none border-slate-200"
                  data-testid="agency-name-input"
                />
              </div>
              <div>
                <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500 mb-2 block">
                  City *
                </label>
                <Select 
                  value={agencyForm.city} 
                  onValueChange={(value) => setAgencyForm({...agencyForm, city: value})}
                >
                  <SelectTrigger className="rounded-none border-slate-200" data-testid="agency-city-select">
                    <SelectValue placeholder="Select city" />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    {cities.map((city) => (
                      <SelectItem key={city} value={city}>{city}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500 mb-2 block">
                Address
              </label>
              <Input
                value={agencyForm.address}
                onChange={(e) => setAgencyForm({...agencyForm, address: e.target.value})}
                placeholder="Full address"
                className="rounded-none border-slate-200"
                data-testid="agency-address-input"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500 mb-2 block">
                  Phone Number
                </label>
                <Input
                  value={agencyForm.phone}
                  onChange={(e) => setAgencyForm({...agencyForm, phone: e.target.value})}
                  placeholder="+91 XXXXX XXXXX"
                  className="rounded-none border-slate-200"
                  data-testid="agency-phone-input"
                />
              </div>
              <div>
                <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500 mb-2 block">
                  Years of Experience
                </label>
                <Input
                  type="number"
                  value={agencyForm.years_experience}
                  onChange={(e) => setAgencyForm({...agencyForm, years_experience: parseInt(e.target.value) || 0})}
                  className="rounded-none border-slate-200"
                  data-testid="agency-experience-input"
                />
              </div>
            </div>

            <div>
              <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500 mb-2 block">
                Base Price (INR)
              </label>
              <Input
                type="number"
                value={agencyForm.base_price}
                onChange={(e) => setAgencyForm({...agencyForm, base_price: parseFloat(e.target.value) || 0})}
                className="rounded-none border-slate-200"
                data-testid="agency-price-input"
              />
            </div>

            <div>
              <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500 mb-2 block">
                Specializations
              </label>
              <div className="flex flex-wrap gap-2">
                {specializations.map((spec) => (
                  <button
                    key={spec}
                    type="button"
                    onClick={() => {
                      const newSpecs = agencyForm.specializations.includes(spec)
                        ? agencyForm.specializations.filter(s => s !== spec)
                        : [...agencyForm.specializations, spec];
                      setAgencyForm({...agencyForm, specializations: newSpecs});
                    }}
                    className={`text-sm px-3 py-1.5 border transition-colors ${
                      agencyForm.specializations.includes(spec)
                        ? 'bg-slate-900 text-white border-slate-900'
                        : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
                    }`}
                    data-testid={`spec-${spec.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    {spec}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500 mb-2 block">
                Description
              </label>
              <Textarea
                value={agencyForm.description}
                onChange={(e) => setAgencyForm({...agencyForm, description: e.target.value})}
                placeholder="Describe your agency and services..."
                className="rounded-none border-slate-200 min-h-[100px]"
                data-testid="agency-description-input"
              />
            </div>

            <Button 
              onClick={showEditProfile ? handleUpdateAgency : handleCreateAgency}
              className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-none h-11"
              data-testid="submit-agency-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {showEditProfile ? 'Save Changes' : 'Create Agency'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manage Plans Dialog */}
      <Dialog open={showManagePlans} onOpenChange={setShowManagePlans}>
        <DialogContent className="rounded-none border-slate-200 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-bold text-slate-900">
              Manage Service Plans
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            {/* Current Plans */}
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500 mb-3">
                Your Plans ({editingPlans?.length || 0})
              </p>
              
              {(!editingPlans || editingPlans.length === 0) ? (
                <div className="bg-slate-50 border border-slate-200 p-6 text-center">
                  <Package className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                  <p className="text-slate-500 text-sm">No plans added yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {editingPlans.map((plan) => (
                    <div key={plan.plan_id} className="border border-slate-200 p-4 flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-display font-bold text-slate-900">{plan.name}</h4>
                          <span className="text-lg font-bold text-emerald-600">{formatPrice(plan.price)}</span>
                        </div>
                        <p className="text-sm text-slate-600 mb-2">{plan.description}</p>
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          <Clock className="w-3 h-3" />
                          {plan.duration}
                        </div>
                        {plan.features && plan.features.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {plan.features.map((feature) => (
                              <span key={feature} className="text-xs bg-slate-100 px-2 py-0.5">{feature}</span>
                            ))}
                          </div>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemovePlan(plan.plan_id)}
                        className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                        data-testid={`remove-plan-${plan.plan_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Add New Plan Form */}
            <div className="border-t border-slate-200 pt-6">
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500 mb-3">
                Add New Plan
              </p>
              
              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500 mb-2 block">
                      Plan Name *
                    </label>
                    <Input
                      value={planForm.name}
                      onChange={(e) => setPlanForm({...planForm, name: e.target.value})}
                      placeholder="e.g., Basic Check, Premium Package"
                      className="rounded-none border-slate-200"
                      data-testid="plan-name-input"
                    />
                  </div>
                  <div>
                    <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500 mb-2 block">
                      Price (INR) *
                    </label>
                    <Input
                      type="number"
                      value={planForm.price}
                      onChange={(e) => setPlanForm({...planForm, price: e.target.value})}
                      placeholder="e.g., 20000"
                      className="rounded-none border-slate-200"
                      data-testid="plan-price-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500 mb-2 block">
                    Duration
                  </label>
                  <Select 
                    value={planForm.duration} 
                    onValueChange={(value) => setPlanForm({...planForm, duration: value})}
                  >
                    <SelectTrigger className="rounded-none border-slate-200" data-testid="plan-duration-select">
                      <SelectValue placeholder="Select duration" />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      <SelectItem value="Per Case">Per Case</SelectItem>
                      <SelectItem value="7-10 Days">7-10 Days</SelectItem>
                      <SelectItem value="15-20 Days">15-20 Days</SelectItem>
                      <SelectItem value="25-30 Days">25-30 Days</SelectItem>
                      <SelectItem value="30-45 Days">30-45 Days</SelectItem>
                      <SelectItem value="Custom">Custom Duration</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500 mb-2 block">
                    Description
                  </label>
                  <Input
                    value={planForm.description}
                    onChange={(e) => setPlanForm({...planForm, description: e.target.value})}
                    placeholder="Brief description of this plan"
                    className="rounded-none border-slate-200"
                    data-testid="plan-description-input"
                  />
                </div>

                <div>
                  <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500 mb-2 block">
                    Features (one per line)
                  </label>
                  <Textarea
                    value={planForm.features}
                    onChange={(e) => setPlanForm({...planForm, features: e.target.value})}
                    placeholder="Identity Verification&#10;Address Check&#10;Family Background&#10;Employment History"
                    className="rounded-none border-slate-200 min-h-[100px]"
                    data-testid="plan-features-input"
                  />
                </div>

                <Button 
                  onClick={handleAddPlan}
                  variant="outline"
                  className="w-full rounded-none border-slate-200"
                  data-testid="add-plan-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Plan
                </Button>
              </div>
            </div>

            {/* Save Button */}
            <Button 
              onClick={handleUpdateAgency}
              className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-none h-11"
              data-testid="save-plans-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              Save All Plans
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bank Details Dialog */}
      <Dialog open={showBankDetails} onOpenChange={setShowBankDetails}>
        <DialogContent className="rounded-none border-slate-200 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-bold text-slate-900">
              Bank Details for Payouts
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <p className="text-sm text-slate-600 bg-slate-50 p-3 border border-slate-200">
              Add your bank details to receive payments from bookings. We deduct 10% platform fee and transfer the remaining amount.
            </p>
            
            <div>
              <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500 mb-2 block">
                Account Holder Name
              </label>
              <Input
                value={bankForm.bank_account_name}
                onChange={(e) => setBankForm({...bankForm, bank_account_name: e.target.value})}
                placeholder="Full name as per bank account"
                className="rounded-none border-slate-200"
                data-testid="bank-account-name-input"
              />
            </div>

            <div>
              <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500 mb-2 block">
                Bank Name
              </label>
              <Input
                value={bankForm.bank_name}
                onChange={(e) => setBankForm({...bankForm, bank_name: e.target.value})}
                placeholder="e.g., HDFC Bank, SBI, ICICI"
                className="rounded-none border-slate-200"
                data-testid="bank-name-input"
              />
            </div>

            <div>
              <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500 mb-2 block">
                Account Number
              </label>
              <Input
                value={bankForm.bank_account_number}
                onChange={(e) => setBankForm({...bankForm, bank_account_number: e.target.value})}
                placeholder="Your bank account number"
                className="rounded-none border-slate-200"
                data-testid="bank-account-number-input"
              />
            </div>

            <div>
              <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500 mb-2 block">
                IFSC Code
              </label>
              <Input
                value={bankForm.bank_ifsc}
                onChange={(e) => setBankForm({...bankForm, bank_ifsc: e.target.value.toUpperCase()})}
                placeholder="e.g., HDFC0001234"
                className="rounded-none border-slate-200"
                data-testid="bank-ifsc-input"
              />
            </div>

            <div>
              <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500 mb-2 block">
                UPI ID (Optional)
              </label>
              <Input
                value={bankForm.upi_id}
                onChange={(e) => setBankForm({...bankForm, upi_id: e.target.value})}
                placeholder="e.g., yourname@upi"
                className="rounded-none border-slate-200"
                data-testid="upi-id-input"
              />
            </div>

            <Button 
              onClick={handleUpdateBankDetails}
              className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-none h-11"
              data-testid="save-bank-details-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              Save Bank Details
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AgencyDashboard;
